/**
 * DUERP-LAYOUT.UTILS
 * Lecture rapide d'un document unique Excel : l'assistant ne recopie pas le document, il decrit la
 * structure de chaque onglet (type d'onglet, unite de travail, ligne d'en-tetes, role de chaque
 * colonne) a partir de ses premieres lignes. Le code lit ensuite toutes les lignes selon cette structure.
 * Une reponse courte par onglet, tous les onglets en parallele : quelques secondes au lieu de minutes.
 */
import * as XLSX from "xlsx"
import { v4 as uuid } from "uuid"
import { matchKey, normalizeDocumentScale, RawDocument, SCALES, withoutUnusedUnits } from "./duerp-mapping.utils"
import { addActionPlan, list, sheetOf, toCost } from "./duerp-reading.utils"

//Lignes non vides envoyees a l'assistant pour decrire un onglet (assez pour une grille de cotation
//complete : gravite, frequence et maitrise se suivent souvent sur une soixantaine de lignes), et
//longueur max d'une cellule
const SAMPLE_ROWS = 60
const SAMPLE_CELL_LENGTH = 100
//Titre d'une mesure : son texte, coupe au-dela (le texte complet passe en description)
const ACTION_NAME_LENGTH = 120

/** Onglet lu en grille : rows[i][j] = texte de la cellule, ligne Excel firstRow + i + 1, colonne firstCol + j */
export interface SheetGrid {
  name: string
  firstRow: number
  firstCol: number
  rows: string[][]
}

const LAYOUT_INSTRUCTIONS = `You get the first rows of one sheet of a French "Document Unique d'Évaluation des Risques Professionnels" (DUERP) workbook. Each row starts with its row number in brackets, each cell with its column letter (e.g. "[6] A: Chutes | B: Sol glissant"). Describe how the sheet is laid out, so that a program can read all of its rows. Never invent anything. Never write a field whose value would be null, "" or [].

- "kind": "risks" when the sheet is a table of risks of one or several work units (one row per risk); "actionPlan" when it is a table of prevention actions to do with their follow-up (deadline, responsible, cost), usually named "plan d'actions"; "legend" when it only explains the scoring scales; "other" for anything else: cover, instructions, summaries, risk hierarchies ("hiérarchiser les risques"), accident follow-ups ("suivi AT") and any recap sheet that repeats risks of other sheets.
- "headerRow": the row number holding the column labels of the table.
- "unit": the work unit ("unité de travail") of the whole sheet, when the sheet is about one unit (often given by the sheet name or a title such as "Unité de travail : Accueil"). "name": its name without the plan. "map": the building, floor or site it belongs to; write a numbered building "Bâtiment <number>" ("B1", "BAT 1" → "Bâtiment 1"). Never a code alone (e.g. "U3 B1"): use the most descriptive name the sheet gives.
- "columns": the column letter of each field of the table, only when the table has it:
  - "unit": the column giving the work unit of each row, when the table lists several units; then "map" (the column of the plan, if any).
  - "family": the risk category.
  - "attributes": the descriptive text columns, the hazard first ("danger", "phénomène dangereux"), then the hazardous situation, the possible harm…, each {"column", "label"} with its label as written.
  - "severity" (gravité), "frequency" (fréquence, probabilité, exposition), "prevention" (maîtrise, niveau de prévention). Identify these columns from their own headers, including a header spread across several rows: G / Indice G = severity; F / Indice F = frequency; M / Indice M = prevention. Column order varies by sheet: do not assume an order or copy a neighbouring column. A column containing "2 - PROBABLE POSSIBLE" is frequency, not severity. A column headed G remains severity even if F is nearby. Never the final score (G x F, G x F x M, risque brut, risque résiduel, criticité, priorité). Omit a column when its role cannot be established from this sheet.
  - "existingMeasures": the columns of measures already in place; "plannedMeasures": the columns of actions to do, recommended or planned.
  - "status", "datePlanned" (échéance, délai), "dateDone" (date de réalisation), "cost", "financedBy", "responsible", "comment".
- "scaleTables": for each scoring table visible in this sheet (including several tables on the same sheet), give its scale ("severity", "frequency" or "prevention"), "firstRow" and "lastRow" of the levels, "valueColumn", "labelColumn" and "descriptionColumns" (column letters). Only describe an explicit legend defining the meaning of each coefficient, NEVER the score columns of a risk register or a list of observed risk ratings. Omit scaleTables when no legend is visible. firstRow and lastRow are mandatory exact row numbers. This lets the program read the original cells without rewriting their values. Gravité G, fréquence F and maîtrise M are three separate scales, even on one sheet.
- "scales": the scoring legend shown in these rows, for "severity", "frequency" and "prevention": every level as {"value", "label", "description"}, the description being its definition or example when given. Preserve every original coefficient: gravity 10/20/30/40 stays 10/20/30/40; maîtrise 0,25/0,5/0,75/1 becomes JSON numbers 0.25/0.5/0.75/1. Never replace coefficients with ranks. Extract ALL three scales when present, independently of the sheet kind. A risk matrix and residual priority thresholds are not scoring scales.

Before answering, check each scale against its own section heading and stop its row range at the end of that section. Never merge the levels of different sections. For example, if GRAVITÉ G defines 10/20/30/40, FRÉQUENCE F defines 1/2/3/4, and MAÎTRISE M defines 0.25/0.5/0.75/1, frequency must contain only its four levels 1/2/3/4. Never append 10/20/30/40 to frequency. These are examples, not defaults: use only coefficients and definitions actually present in the supplied cells. Do not infer extra legend levels from risk rows. Keep each original label and definition under its own coefficient.`

export function getLayoutPreprompt() {
  const format = {
    kind: "risks | actionPlan | legend | other",
    headerRow: 1,
    unit: { name: "", map: "" },
    columns: {
      unit: "",
      map: "",
      family: "",
      attributes: [{ column: "", label: "" }],
      severity: "",
      frequency: "",
      prevention: "",
      existingMeasures: [""],
      plannedMeasures: [""],
      status: "",
      datePlanned: "",
      dateDone: "",
      cost: "",
      financedBy: "",
      responsible: "",
      comment: ""
    },
    scaleTables: [{ scale: "severity", firstRow: 7, lastRow: 10, valueColumn: "B", labelColumn: "C", descriptionColumns: ["D", "G"] }],
    scales: { severity: [{ value: 1, label: "", description: "" }], frequency: [], prevention: [] }
  }
  return `${LAYOUT_INSTRUCTIONS}

Answer only with one JSON object in this format:
${JSON.stringify(format)}`
}

//Date d'Excel en date ISO, le reste en texte
function cellText(value: unknown) {
  if (value instanceof Date) return value.toISOString().slice(0, 10)
  return String(value ?? "").trim()
}

/**
 * Classeur -> une grille par onglet non vide. Une cellule fusionnee prend sa valeur sur toute sa
 * surface : une famille fusionnee sur plusieurs lignes s'applique a chacune.
 */
export async function readGrids(file: File): Promise<SheetGrid[]> {
  const workbook = XLSX.read(await file.arrayBuffer(), { cellDates: true })
  return workbook.SheetNames.map((name) => {
    const sheet = workbook.Sheets[name]
    for (const merge of sheet["!merges"] ?? []) {
      const origin = sheet[XLSX.utils.encode_cell(merge.s)]
      if (!origin) continue
      for (let r = merge.s.r; r <= merge.e.r; r++)
        for (let c = merge.s.c; c <= merge.e.c; c++) sheet[XLSX.utils.encode_cell({ r, c })] = origin
    }
    const range = sheet["!ref"] ? XLSX.utils.decode_range(sheet["!ref"]) : null
    const rows = XLSX.utils
      .sheet_to_json<unknown[]>(sheet, { header: 1, blankrows: true, defval: "", raw: true })
      .map((cells) => cells.map(cellText))
    return { name, firstRow: range?.s.r ?? 0, firstCol: range?.s.c ?? 0, rows }
  }).filter((grid) => grid.rows.some((cells) => cells.some(Boolean)))
}

//Premieres lignes de l'onglet, chaque cellule precedee de sa lettre de colonne, sur une seule ligne
export function getLayoutPrompt(grid: SheetGrid, sheetNames: string[]) {
  const sample = grid.rows
    .map((cells, index) => {
      const values = cells
        .map((cell, column) =>
          cell
            ? `${XLSX.utils.encode_col(grid.firstCol + column)}: ${cell.replace(/\s*\n\s*/g, " / ").slice(0, SAMPLE_CELL_LENGTH)}`
            : ""
        )
        .filter(Boolean)
      return values.length ? `[${grid.firstRow + index + 1}] ${values.join(" | ")}` : ""
    })
    .filter(Boolean)
    .slice(0, SAMPLE_ROWS)
  return `Onglets du classeur : ${sheetNames.join(", ")}.\n\n### Onglet « ${grid.name} »\n${sample.join("\n")}`
}

//Score : nombre en tete de cellule ("3", "3 - Grave"), sinon libelle de la legende du document
function toScore(text: string, legend: { value: number; label: string }[]) {
  const number = text.match(/^\s*(\d+(?:[.,]\d+)?)/)
  if (number) return Number(number[1].replace(",", "."))
  return legend.find((level) => level.label && matchKey(level.label) === matchKey(text))?.value ?? null
}

//Date ISO (cellule date d'Excel) ou jj/mm/aaaa ; sinon null, le texte reste en description
function toDate(text: string) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text
  const date = text.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/)
  if (!date) return null
  const year = date[3].length === 2 ? `20${date[3]}` : date[3]
  return `${year}-${date[2].padStart(2, "0")}-${date[1].padStart(2, "0")}`
}

//Une cellule de mesures en contient souvent plusieurs, une par ligne ou par puce
function splitMeasures(text: string) {
  return text
    .split(/\r?\n/)
    .map((line) => line.replace(/^[\s\-–—•▪●◦*·>]+/, "").trim())
    .filter((line) => line && line !== "0")
}

function toStatus(text: string, dateDone: string | null) {
  const status = matchKey(text)
  if (dateDone || /\b(fait|realise|termine|clo)\b/.test(status)) return "done"
  if (/\ben cour\b/.test(status)) return "pending"
  return "todo"
}

/** Structures des onglets + grilles -> lecture brute, au meme format que la lecture par l'assistant */
export function layoutsToRawDocument(grids: SheetGrid[], layouts: any[]): RawDocument {
  // Chaque onglet fournit un candidat : une cotation répétée dans les risques ne doit jamais
  // écraser la grille d'évaluation. Dans une grille, lire les cellules avant la transcription IA.
  const candidates = layouts.map((layout, index) => {
    const grid = grids[index]
    const scales = Object.fromEntries(SCALES.map((scale) => [scale, normalizeDocumentScale(list(layout?.scales?.[scale]))]))
    if (!grid) return { kind: layout?.kind, scales }
    const cell = (row: string[], column: unknown) =>
      typeof column === "string" && /^[A-Z]+$/i.test(column)
        ? row[XLSX.utils.decode_col(column.toUpperCase()) - grid.firstCol] ?? ""
        : ""
    for (const table of list(layout?.scaleTables)) {
      if (!SCALES.includes(table?.scale)) continue
      // Des bornes absentes faisaient lire tout l'onglet, y compris toutes les cotations des risques.
      if (!Number.isInteger(table.firstRow) || !Number.isInteger(table.lastRow) ||
          table.firstRow < grid.firstRow + 1 || table.lastRow < table.firstRow ||
          table.lastRow > grid.firstRow + grid.rows.length) continue
      if (typeof table.valueColumn !== "string" || typeof table.labelColumn !== "string" ||
          table.valueColumn.toUpperCase() === table.labelColumn.toUpperCase()) continue
      const isRiskColumn = layout?.kind === "risks" || layout?.kind === "actionPlan"
      if (isRiskColumn && table.lastRow > layout.headerRow &&
          table.valueColumn.toUpperCase() === String(layout.columns?.[table.scale] ?? "").toUpperCase()) continue
      const levels = normalizeDocumentScale(grid.rows.flatMap((row, index) => {
        const number = grid.firstRow + index + 1
        if (number < table.firstRow || number > table.lastRow) return []
        const text = cell(row, table.valueColumn).trim().replace(",", ".")
        const value = text ? Number(text) : NaN
        const label = cell(row, table.labelColumn)
        const description = [...new Set(list(table.descriptionColumns)
          .filter((column) => String(column).toUpperCase() !== table.labelColumn.toUpperCase() &&
            String(column).toUpperCase() !== table.valueColumn.toUpperCase())
          .map((column) => cell(row, column)).filter(Boolean))].join("\n")
        return [{ value, label, description }]
      }))
      if (levels.length) scales[table.scale] = levels
    }
    return { kind: layout?.kind, scales }
  })
  const byLegend = [...candidates.filter((candidate) => candidate.kind === "legend"),
    ...candidates.filter((candidate) => candidate.kind !== "legend")]
  const scales = Object.fromEntries(SCALES.map((scale) => [
    scale, byLegend.find((candidate) => candidate.scales[scale]?.length)?.scales[scale] ?? []
  ]))

  //Unites de travail, rapprochees par nom et plan
  const units = new Map<string, RawDocument["units"][number]>()
  function getUnit(name: string, map: string) {
    const key = `${matchKey(name)}|${matchKey(map)}`
    if (!units.has(key)) units.set(key, { id: uuid(), name: name.trim(), map: map.trim() || null })
    return units.get(key)!
  }

  //Lignes du tableau d'un onglet, a partir de sa ligne d'en-tetes, avec la lecture de leurs cellules
  function tableRows(grid: SheetGrid, layout: any) {
    const columns = layout?.columns ?? {}
    const column = (letter: unknown) =>
      typeof letter === "string" && letter ? XLSX.utils.decode_col(letter.toUpperCase()) - grid.firstCol : -1
    //Index de la ligne d'en-tetes dans la grille (ligne Excel = firstRow + index + 1)
    const start = Math.max(0, Number(layout?.headerRow ?? 0) - 1 - grid.firstRow)
    return grid.rows.slice(start).map((cells, index) => ({
      number: grid.firstRow + start + index + 1,
      get: (letter: unknown) => cells[column(letter)] ?? "",
      getAll: (letters: unknown) => list(letters).flatMap((letter) => splitMeasures(cells[column(letter)] ?? "")),
      columns
    }))
  }

  const toAction = (text: string, status: string, row: ReturnType<typeof tableRows>[number]) => {
    const details = [
      row.get(row.columns.responsible) && `Responsable : ${row.get(row.columns.responsible)}`,
      row.get(row.columns.comment) && `Commentaire : ${row.get(row.columns.comment)}`,
      !toDate(row.get(row.columns.datePlanned)) &&
        row.get(row.columns.datePlanned) &&
        `Échéance : ${row.get(row.columns.datePlanned)}`
    ].filter(Boolean)
    const isLong = text.length > ACTION_NAME_LENGTH
    return {
      name: isLong ? `${text.slice(0, ACTION_NAME_LENGTH - 1).trimEnd()}…` : text,
      description: [isLong ? text : "", ...(status === "done" ? [] : details)].filter(Boolean).join("\n"),
      status: status as "todo" | "pending" | "done",
      dateStarted: null,
      datePlanned: status === "done" ? null : toDate(row.get(row.columns.datePlanned)),
      dateDone: status === "done" ? toDate(row.get(row.columns.dateDone)) : null,
      cost: status === "done" ? null : toCost(row.get(row.columns.cost)),
      financedBy: status === "done" ? null : row.get(row.columns.financedBy) || null
    }
  }

  const risks: RawDocument["risks"] = []
  grids.forEach((grid, index) => {
    const layout = layouts[index]
    const attributes = list(layout?.columns?.attributes).filter((attribute: any) => attribute?.column)
    if (layout?.kind !== "risks" || !attributes.length) return
    let family = ""
    let unitName = ""
    for (const row of tableRows(grid, layout).slice(1)) {
      const values = attributes.map((attribute: any) => [
        String(attribute.label || attribute.column),
        row.get(attribute.column)
      ])
      if (!values.some(([, value]) => value)) continue
      //Titre de section fusionne sur toute la ligne : la meme valeur dans chaque colonne
      const filled = values.map(([, value]) => value).filter(Boolean)
      if (filled.length > 1 && new Set(filled).size === 1) continue

      family = row.get(row.columns.family) || family
      unitName = row.get(row.columns.unit) || unitName
      const unit = row.columns.unit
        ? getUnit(unitName || grid.name, row.get(row.columns.map))
        : getUnit(layout.unit?.name || grid.name, layout.unit?.name ? (layout.unit.map ?? "") : "")
      const dateDone = toDate(row.get(row.columns.dateDone))
      const plannedStatus = toStatus(row.get(row.columns.status), dateDone)

      risks.push({
        sources: [`${grid.name}!${row.number}`],
        family: family || null,
        RiskFamilyId: null,
        attributes: Object.fromEntries(values.filter(([, value]) => value)),
        severity: toScore(row.get(row.columns.severity), scales.severity),
        frequency: toScore(row.get(row.columns.frequency), scales.frequency),
        prevention: toScore(row.get(row.columns.prevention), scales.prevention),
        preventionActions: [
          ...row.getAll(row.columns.existingMeasures).map((text) => toAction(text, "done", row)),
          ...row.getAll(row.columns.plannedMeasures).map((text) => toAction(text, plannedStatus, row))
        ],
        units: [unit.id]
      })
    }
  })

  //Plan d'actions : chaque action rejoint le risque de meme unite et de meme danger, sinon cree le sien
  const unitById = new Map(Array.from(units.values()).map((unit) => [unit.id, unit]))
  const unitCode = (value: string) => matchKey(value).replace(/\s+/g, "")
  const sameUnit = (risk: RawDocument["risks"][number], unit: string) =>
    !unit ||
    unitCode(sheetOf(risk.sources?.[0])) === unitCode(unit) ||
    risk.units.some((id) => matchKey(unitById.get(id)?.name) === matchKey(unit))
  const items: any[] = []
  grids.forEach((grid, index) => {
    const layout = layouts[index]
    if (layout?.kind !== "actionPlan") return
    let unitName = ""
    for (const row of tableRows(grid, layout).slice(1)) {
      unitName = row.get(row.columns.unit) || unitName
      const unit = unitName || layout.unit?.name || ""
      const hazard = row.get(list(row.columns.attributes)[0]?.column)
      const dateDone = toDate(row.get(row.columns.dateDone))
      const riskIndex = risks.findIndex(
        (risk) => sameUnit(risk, unit) && matchKey(Object.values(risk.attributes)[0]) === matchKey(hazard)
      )
      row.getAll(row.columns.plannedMeasures).forEach((text) =>
        items.push({
          ...toAction(text, toStatus(row.get(row.columns.status), dateDone), row),
          source: `${grid.name}!${row.number}`,
          riskId: hazard && riskIndex >= 0 ? `r${riskIndex}` : null,
          unit,
          hazard,
          family: row.get(row.columns.family) || null
        })
      )
    }
  })

  const document = addActionPlan(
    {
      units: Array.from(units.values()),
      risks,
      scales
    },
    [{ actions: items }]
  )
  return withoutUnusedUnits(document)
}
